export type PatientServiceFileType = "pdf" | "xlsx" | "xls" | "image" | "unknown";

const MIME_BY_FILE_TYPE: Record<PatientServiceFileType, string> = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  image: "image/png",
  unknown: "application/octet-stream",
};

const FILE_NAME_BY_TYPE: Record<PatientServiceFileType, string> = {
  pdf: "patient-report.pdf",
  xlsx: "patient-report.xlsx",
  xls: "patient-report.xls",
  image: "patient-report.png",
  unknown: "patient-report.bin",
};

export function normalizePatientServiceFileType(
  type?: string | null,
): PatientServiceFileType | null {
  if (!type) return null;
  const normalized = type.trim().toLowerCase();

  if (normalized === "pdf" || normalized === "application/pdf") return "pdf";
  if (
    normalized === "xlsx" ||
    normalized === "excel" ||
    normalized === "spreadsheet" ||
    normalized ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return "xlsx";
  }
  if (
    normalized === "xls" ||
    normalized === "application/vnd.ms-excel"
  ) {
    return "xls";
  }
  if (
    normalized === "image" ||
    normalized === "png" ||
    normalized === "jpg" ||
    normalized === "jpeg" ||
    normalized === "webp"
  ) {
    return "image";
  }

  return null;
}

export function detectBase64FileType(base64: string): PatientServiceFileType {
  try {
    const raw = base64.includes(",") ? base64.split(",")[1] : base64;
    const decoded = atob(raw);
    if (decoded.startsWith("%PDF")) return "pdf";
    if (decoded.startsWith("PK")) return "xlsx";
    if (decoded.startsWith("\x89PNG")) return "image";
    if (decoded.startsWith("\xFF\xD8\xFF")) return "image";
    if (decoded.startsWith("GIF8")) return "image";
  } catch {
    return "unknown";
  }
  return "unknown";
}

export function resolvePatientServiceFile(
  declaredType: string | null | undefined,
  base64: string,
): PatientServiceFileType {
  const declared = normalizePatientServiceFileType(declaredType);
  const detected = detectBase64FileType(base64);

  if (declared === "pdf" && (detected === "xlsx" || detected === "xls")) {
    return detected;
  }

  return declared ?? detected ?? "unknown";
}

export function isPatientServiceFileType(
  type?: string | null,
): type is PatientServiceFileType {
  return normalizePatientServiceFileType(type) !== null;
}

export function isPatientServiceFileResponse(
  promptType: string | null | undefined,
  declaredType?: string | null,
): boolean {
  if (promptType === "pdf") return true;
  return isPatientServiceFileType(declaredType);
}

export function base64ToBlob(
  base64: string,
  fileType: PatientServiceFileType,
): Blob | null {
  try {
    const raw = base64.includes(",") ? base64.split(",")[1] : base64;
    const byteCharacters = atob(raw);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(null)
      .map((_, index) => byteCharacters.charCodeAt(index));
    return new Blob([new Uint8Array(byteNumbers)], {
      type: MIME_BY_FILE_TYPE[fileType],
    });
  } catch {
    return null;
  }
}

export function downloadBase64File(
  base64: string,
  fileType: PatientServiceFileType,
  fileName?: string,
) {
  const blob = base64ToBlob(base64, fileType);
  if (!blob) return false;

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName || FILE_NAME_BY_TYPE[fileType];
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
  return true;
}

export function getPatientServiceFileMimeType(
  fileType: PatientServiceFileType,
): string {
  return MIME_BY_FILE_TYPE[fileType];
}
